import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useDrag, useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import imagecompression from 'browser-image-compression';

export default function Uploader({ fileType, value = [], onChange, onRemove, onMove, isFullWidth = false }) {
    const toast = useRef(null);
    const fileUploadRef = useRef(null);
    const [totalSize, setTotalSize] = useState(0);
    const isPDF = fileType === ".pdf";
    if(!value){
        value = [];
    }

    const { t } = useTranslation();

    const itemTemplate = (file, props) => {
        return (
            <div className="file-item-container">
                <div className="file-item">
                    {isPDF ? (
                        <div className="file-preview">
                            <FontAwesomeIcon icon={faFilePdf} className="file-icon" />
                            <span id={`file-name-${file.name}`} className="file-name">
                                {file.name}
                            </span>
                        </div>
                    ) : (
                        <img
                            alt={file?.name}
                            role="presentation"
                            src={file?.objectUrl}
                            className="file-image"
                        />
                    )}
                    <Button
                        type="button"
                        icon={<FontAwesomeIcon icon={faTimes} />}
                        className="remove-img-btn"
                        onClick={props.onRemove}
                    />
                </div>
            </div>
        );
    };

    const onTemplateSelect = (e) => {
        const selectedFiles = Array.from(e.files);

        const existingFileNames = new Set(value?.map(file => file.name));
    
        const uniqueFiles = selectedFiles.filter(file => {
            if (existingFileNames?.has(file.name)) {
                return false; 
            }
            return true; 
        });
    
        if (uniqueFiles.length === 0) {
            return;
        }
    
        const promises = uniqueFiles.map(async (file) => {
            let processedFile = file;
            //fare anche per pdf(?)
            if (!isPDF && fileType !== ".mp4") {
                const options = {
                    maxSizeMB: 5,              
                    maxWidthOrHeight: 1920,
                    useWebWorker: true
                };
                processedFile = await imagecompression(file, options);
            }
            
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve({
                        name: processedFile.name,
                        base64: reader.result.split(',')[1],
                        objectUrl: URL.createObjectURL(processedFile),
                        size: processedFile.size
                    });
                };
                reader.onerror = (error) => reject(error);
                reader.readAsDataURL(processedFile);
            });
        });
    
        Promise.all(promises)
            .then((newFiles) => {
                const updatedFiles = [...value, ...newFiles];
                onChange(updatedFiles);
    
                const addedSize = newFiles.reduce((acc, file) => acc + (file.size ?? 0), 0);
                setTotalSize(prevSize => prevSize + addedSize);
            })
            .catch(err => {
                console.error('Errore nel processamento dei file:', err);
            });
    };

    const onTemplateRemove = (file) => {
        if(file){
            const fileIndex = value && value.length > 0 ? value?.findIndex((f) => f === file) : -1;
            if (fileIndex !== -1) {
                onRemove(fileIndex);
                setTotalSize(totalSize - (file?.size ?? 0));
                if(file?.objectUrl){
                    URL.revokeObjectURL(file?.objectUrl);
                }
            }
        }
    };

    const DragItem = ({ index, file }) => {
        const [, drag] = useDrag({
            type: "IMAGE",
            item: { index },
        });
        
        const [, drop] = useDrop({
            accept: "IMAGE",
            hover: (item) => {
                if (item.index !== index) {
                    onMove(item.index, index);
                    item.index = index;
                }
            },
        });
    
        return (
            <div ref={(node) => drag(drop(node))} className="uploader-drag-drop">
                {itemTemplate(file, { onRemove: () => onTemplateRemove(file) })}
            </div>
        );
    };

    const chooseOptions = { icon: <FontAwesomeIcon icon={faPlus} className="pe-2" />, iconOnly: false, className: 'custom-transparent-btn p-button-rounded p-button-outlined' };

    const headerTemplate = (options) => {
        const { className, chooseButton } = options;
        return (
            <div className={`${className} bg-transparent d-flex justify-content-center`}>
                {chooseButton}
            </div>
        );
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Toast ref={toast}></Toast>
            <div className="container">
                <div className="row">
                    {value && Array.isArray(value) && value?.length > 1 ? value.map((file, index) => (
                        <div key={index} className={`${isFullWidth ? "col-lg-1" : "col-lg-2"} me-5 me-lg-4 col-md-2 col-12`}>
                            <DragItem index={index} file={file} />
                        </div>
                    )) : value && Array.isArray(value) && value?.length > 0 && value.map((file, index) => (
                        <div key={index} className={`${isFullWidth ? "col-lg-1" : "col-lg-2"} me-5 me-lg-4 col-md-2 col-12`}>
                            <div className="uploader-drag-drop">
                                {itemTemplate(file, { onRemove: () => onTemplateRemove(file) })}
                            </div>
                        </div>
                    ))
                    }
                </div>
            </div>
            <FileUpload
                ref={fileUploadRef}
                name="demo[]"
                url="/api/upload"
                multiple
                accept={!fileType ? "image/*" : fileType === ".mp4" ? "image/*, .mp4" : fileType}
                maxFileSize={104857600}
                onSelect={onTemplateSelect}
                headerTemplate={headerTemplate}
                className="d-flex justify-content-center"
                chooseOptions={chooseOptions}
                chooseLabel={!isPDF ?  fileType === ".mp4" ? t("UploadImageOrVideo") : t("UploadImage") : t("UploadFile")}
            />
         </DndProvider>
    );
}